import { BaseError, type BaseErrorOptions } from '@canvas/error';
import { sleep } from '@canvas/sleep';

export type UnrecoverableNetworkErrorType =
  | 'timeout'
  | 'bad-request'
  | 'unknown'
  | 'not-found';

/**
 * Unrecoverable network error that we do not attempt to retry
 */
export class UnrecoverableNetworkError extends BaseError {
  constructor(
    message: string,
    readonly errorType: UnrecoverableNetworkErrorType = 'unknown',
    options?: BaseErrorOptions
  ) {
    super({ name: 'UnrecoverableNetworkError', ...options }, message);
  }
}

/**
 * Recoverable network error that we may attempt to retry
 */
export class RecoverableNetworkError extends BaseError {
  constructor(message: string, options?: BaseErrorOptions) {
    super({ name: 'RecoverableNetworkError', ...options }, message);
  }
}

/**
 * Check if an error is a recoverable network error
 * @param error The error to check
 * @returns whether the error is a recoverable network error
 */
export const isRecoverableNetworkError = (error: any) =>
  error instanceof RecoverableNetworkError;

export const DEFAULT_RETRY_TIME_MS = 500;
export const DEFAULT_RETRY_BACKOFF = 1.5;
export const DEFAULT_RETRY_MAX_MS = 60_000;

/**
 * Compute how long to wait for subsequent retries
 *
 * Uses both backoff and jitter to smooth reconnect load
 *
 * @param attempt The attempt number, starting at 1
 * @param retryTimeMs The default time between retries
 * @param retryBackoff The factor by which to increase retries
 * @param retryMaxMs The maximum retry time
 */
export function getRetryTimeout(
  attempt: number,
  retryTimeMs = DEFAULT_RETRY_TIME_MS,
  retryBackoff = DEFAULT_RETRY_BACKOFF,
  retryMaxMs = DEFAULT_RETRY_MAX_MS
) {
  function getBackoff(attempt: number) {
    return Math.min(retryTimeMs * Math.pow(retryBackoff, attempt - 1), retryMaxMs);
  }

  const backoff = getBackoff(attempt);
  let nextBackoff = getBackoff(attempt + 1);
  if (nextBackoff === backoff) {
    // If the next backoff is the same as the current backoff, then no jitter would be
    // applied.
    nextBackoff = backoff + retryMaxMs;
  }

  const jitterFactor = Math.random();

  // Jitter is somewhere between the current backoff and the next backoff to get a good spread
  const jitter = (nextBackoff - backoff) * jitterFactor;

  return backoff + jitter;
}

type RetryResult<T> =
  | {
      success: true;
      result: T;
    }
  | {
      success: false;
      error: Error | unknown;
    };

interface RetryOptions<T> {
  callback: (attempt: number) => Promise<T>;
  maxRetries: number;
  retryTimeMs?: number;
  retryBackoff?: number;
  retryMaxMs?: number;
  shouldRetry?: (error: Error) => Promise<boolean> | boolean;
  signal?: AbortSignal;
}

/**
 * A utility function to retry an async action, such as a request, with a backoff that uses both backoff and jitter.
 *
 * @param params.callback - the callback to retry
 * @param params.shouldRetry - return a boolean indicating if a retry should be attempted
 * @param params.retryTimeMs - time to retry, defaults to 500ms
 * @param params.retryBackoff - The factor by which to increase retries, defaults to 1.5
 * @param params.retryMaxMs - the maximum retry time, defaults to 60s
 * @param params.maxRetries - the maximum number of retries. Use Number.POSITIVE_INFINITY to retry infinitely
 * @param params.signal - an abort signal to abort the retry
 * @returns - a promise containing the result of the successful retry
 */
export async function retryWithBackoff<T>({
  shouldRetry = () => true,
  retryTimeMs,
  retryBackoff,
  retryMaxMs,
  maxRetries,
  callback,
  signal,
}: RetryOptions<T>): Promise<T> {
  async function runAttempt(attempt: number): Promise<RetryResult<T>> {
    try {
      const result = await callback(attempt);
      return {
        success: true,
        result,
      };
    } catch (e) {
      const avoidSlowNetworkFailureNetworkMocks =
        globalThis.process?.env?.MODE === 'test';
      if (avoidSlowNetworkFailureNetworkMocks) {
        throw e;
      }

      if (attempt >= maxRetries) {
        // If we've reached the maximum number of retries, don't sleep again
        return {
          success: false,
          error: e,
        };
      }

      if (e instanceof Error && (await shouldRetry(e))) {
        await sleep(getRetryTimeout(attempt, retryTimeMs, retryBackoff, retryMaxMs));

        return {
          success: false,
          error: e,
        };
      } else {
        throw e;
      }
    }
  }

  // This CANNOT use recursion, as it will blow the stack with a large number of retries
  let lastError: Error | unknown | undefined = undefined;
  let attempt = 0;
  do {
    if (signal?.aborted) {
      if (lastError) {
        throw lastError;
      } else {
        signal.throwIfAborted();
      }
    }

    const result = await runAttempt(attempt + 1);
    if (result.success) {
      return result.result;
    } else {
      lastError = result.error;
    }

    attempt++;
  } while (attempt < maxRetries);

  throw lastError;
}

export interface WaitForRetryOptions {
  attempt: number;
  retryTimeMs?: number;
  retryBackoff?: number;
  retryMaxMs?: number;
}

export async function waitForRetry({
  attempt,
  retryTimeMs = DEFAULT_RETRY_TIME_MS,
  retryBackoff = DEFAULT_RETRY_BACKOFF,
  retryMaxMs = DEFAULT_RETRY_MAX_MS,
}: WaitForRetryOptions): Promise<void> {
  if (attempt === 0) {
    return;
  }

  await sleep(getRetryTimeout(attempt, retryTimeMs, retryBackoff, retryMaxMs));
}
